import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { LanguageMenu } from './LanguageMenu';
import { ThemeToggle } from './ThemeToggle';
import type { NavItem } from './navItems';

interface HeaderMenuProps {
  /** The pages to offer. Handed down rather than read here, so the bar and the menu offer the same ones. */
  items: readonly NavItem[];
  name?: string;
  email?: string;
  onSignOut: () => void;
}

/**
 * What the top bar's row of buttons becomes on a narrow screen: one button that opens a menu holding all of
 * it - who is signed in, the pages, the language, and signing out. Which of the two the shell renders is
 * Layout's decision; this is only the narrow one.
 *
 * Driven from React rather than Bootstrap's dropdown JavaScript - the classes are Bootstrap's, the open and
 * close is ours, like the language menu beside it.
 */
export function HeaderMenu({ items, name, email, onSignOut }: HeaderMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    // A click anywhere else closes it. Inside is left alone on purpose: the language switcher lives in here
    // and opening it must not take the menu around it away.
    function onPointerDown(event: MouseEvent) {
      if (!container.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    // Escape closes it and gives the button the focus back, or the keyboard is left inside a menu that is gone.
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        toggle.current?.focus();
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  return (
    <div className="actions collapsed dropdown" ref={container}>
      <button
        ref={toggle}
        type="button"
        className="btn btn-primary hamburger-btn"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={t('nav.menu')}
        title={t('nav.menu')}
        onClick={() => setOpen((value) => !value)}
      >
        <i className="pi pi-align-left" aria-hidden="true" />
      </button>

      {/* Rendered only while open, rather than always present and hidden by a class. Both are correct in a
          browser - Bootstrap's closed menu is display:none, which hides it from assistive technology too -
          but only this one can be checked without a stylesheet, and jsdom has none. */}
      {open && (
        <ul className="dropdown-menu dropdown-menu-end show">
          {/* Who is signed in. The bar shows this beside the logo, and there is no room for it here. */}
          {(name || email) && (
            <>
              <li className="dropdown-user-infos">
                {name && <span className="fw-semibold">{name}</span>}
                {email && <span className="text-secondary">{email}</span>}
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>
            </>
          )}

          {items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `dropdown-item${isActive ? ' active' : ''}`}
                // Going somewhere always closes it: the menu covers the page it has just navigated to.
                onClick={() => setOpen(false)}
              >
                <i className={`pi ${item.icon}`} aria-hidden="true" />
                {t(item.labelKey)}
              </NavLink>
            </li>
          ))}

          <li>
            <hr className="dropdown-divider" />
          </li>

          <li className="mobile-language-select">
            <LanguageMenu variant="menu" />
          </li>

          <li className="mobile-theme-toggle">
            <ThemeToggle variant="menu" />
          </li>

          <li>
            <hr className="dropdown-divider" />
          </li>

          <li>
            <button type="button" className="dropdown-item text-danger" onClick={onSignOut}>
              <i className="pi pi-sign-out" aria-hidden="true" />
              {t('nav.signOut')}
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}
